import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'

import { StaffRoute } from './StaffRoute.js'
import { StaffShell } from './StaffShell.js'
import { StaffLoginPage } from './pages/StaffLoginPage.js'
import { StaffHomePage } from './pages/StaffHomePage.js'
import { StaffScanPage } from './pages/StaffScanPage.js'
import { StaffEnrollPage } from './pages/StaffEnrollPage.js'
import { StaffIssuePage } from './pages/StaffIssuePage.js'
import { useApplyOwnerTheme } from '../owner/theme/useOwnerTheme.js'
import '../owner/theme/ownerTokens.css'

// Staff is part of the owner-side app organizationally — it shares the
// `.owner-root` theme scope and the `useOwnerTheme` hook, just a different
// route subtree and its own QueryClient.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: true, retry: 1 },
  },
})

/**
 * Staff sub-application. Mounted by the top-level router at `/staff/*`, so the
 * routes here are relative to `/staff`:
 *   /staff/login         public — slug + email + password
 *   /staff               gated  — home (Stamp / Enroll cards)
 *   /staff/scan          gated  — scanner (StaffScanPage)
 *   /staff/enroll        gated  — collect details + mint enrollment-token QR
 *   /staff/issue/:cardId gated  — direct-issuance stub (V1 redirects to scanner)
 */
export function StaffApp() {
  useApplyOwnerTheme()

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="login" element={<StaffLoginPage />} />
        <Route
          element={
            <StaffRoute>
              <StaffShell />
            </StaffRoute>
          }
        >
          <Route index element={<StaffHomePage />} />
          <Route path="scan" element={<StaffScanPage />} />
          <Route path="enroll" element={<StaffEnrollPage />} />
          <Route path="issue/:cardId" element={<StaffIssuePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/staff" replace />} />
      </Routes>
      <Toaster
        position="top-center"
        richColors
        offset="calc(env(safe-area-inset-top, 0px) + 16px)"
      />
    </QueryClientProvider>
  )
}
